// /opt/projects/saas_odonto/frontend/src/pages/Campanhas.tsx
// Página de gestão de campanhas de templates WhatsApp

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Plus, Play, Pause, Trash2, Edit, Users, Clock,
    MessageSquare, Target, CheckCircle, RefreshCw,
    AlertCircle, ChevronDown, ChevronUp,
    User, Phone, AlertTriangle, X
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { TagsService } from '../services/apiTags.ts';
import { syncWhatsAppTemplates } from '../services/api.ts';
import {
    getTemplateCampaigns,
    getApprovedTemplates,
    getAudiencePreview,
    createTemplateCampaign,
    updateTemplateCampaign,
    launchTemplateCampaign,
    pauseTemplateCampaign,
    resumeTemplateCampaign,
    deleteTemplateCampaign,
    getCampaignStats,
    getCampaignExecutions,
    TemplateCampaign,
    ApprovedTemplate,
    AudiencePreview,
    TemplateCampaignCreate,
    TemplateCampaignUpdate,
    CampaignStats,
    CampaignExecution
} from '../services/templateCampaignApi.ts';

// =============================================================================
// TYPES
// =============================================================================

interface Tag {
    id: number;
    name: string;
    color: string;
    category: string;
}

// =============================================================================
// HELPERS
// =============================================================================

const WEEKDAYS = [
    { value: 0, label: 'Dom', short: 'D' },
    { value: 1, label: 'Seg', short: 'S' },
    { value: 2, label: 'Ter', short: 'T' },
    { value: 3, label: 'Qua', short: 'Q' },
    { value: 4, label: 'Qui', short: 'Q' },
    { value: 5, label: 'Sex', short: 'S' },
    { value: 6, label: 'Sáb', short: 'S' },
];

const STATUS_CONFIG = {
    draft: { label: 'Rascunho', color: 'gray', icon: Edit },
    running: { label: 'Em Execução', color: 'green', icon: Play },
    paused: { label: 'Pausada', color: 'yellow', icon: Pause },
    completed: { label: 'Concluída', color: 'blue', icon: CheckCircle },
};

// =============================================================================
// COMPONENTS
// =============================================================================

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.draft;
    const Icon = config.icon;

    const colorClasses = {
        gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
        green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    };

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${colorClasses[config.color as keyof typeof colorClasses]}`}>
            <Icon className="w-3 h-3" />
            {config.label}
        </span>
    );
};

const ProgressBar: React.FC<{ sent: number; failed: number; total: number }> = ({ sent, failed, total }) => {
    const sentPercent = total > 0 ? (sent / total) * 100 : 0;
    const failedPercent = total > 0 ? (failed / total) * 100 : 0;

    return (
        <div className="w-full">
            <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-500 dark:text-gray-400">
                    {sent}/{total} enviados
                </span>
                {failed > 0 && (
                    <span className="text-red-500">{failed} falhas</span>
                )}
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full flex">
                    <div
                        className="bg-green-500 transition-all duration-300"
                        style={{ width: `${sentPercent}%` }}
                    />
                    <div
                        className="bg-red-500 transition-all duration-300"
                        style={{ width: `${failedPercent}%` }}
                    />
                </div>
            </div>
        </div>
    );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const CampanhasPage: React.FC = () => {
    const { isDark } = useTheme();
    const clinicId = parseInt(localStorage.getItem('clinic_id') || '0');

    // State
    const [campaigns, setCampaigns] = useState<TemplateCampaign[]>([]);
    const [templates, setTemplates] = useState<ApprovedTemplate[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [audiencePreview, setAudiencePreview] = useState<AudiencePreview | null>(null);
    const [actionLoading, setActionLoading] = useState<number | null>(null);
    const createDialogRef = useRef<HTMLDivElement>(null);
    const creatingCampaignRef = useRef(false);
    creatingCampaignRef.current = actionLoading === -1;

    useEffect(() => {
        if (!showCreateModal) return;
        const previousFocus = document.activeElement as HTMLElement | null;
        createDialogRef.current?.querySelector<HTMLInputElement>('input')?.focus();
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !creatingCampaignRef.current) setShowCreateModal(false);
            if (event.key !== 'Tab') return;
            const controls = createDialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled)');
            if (!controls?.length) return;
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            previousFocus?.focus();
        };
    }, [showCreateModal]);

    // Execution Details State
    const [expandedCampaignId, setExpandedCampaignId] = useState<number | null>(null);
    const [executionsMap, setExecutionsMap] = useState<Record<number, CampaignExecution[]>>({});
    const [loadingExecutions, setLoadingExecutions] = useState<Record<number, boolean>>({});
    const [deleteBlockedModal, setDeleteBlockedModal] = useState<{ show: boolean; credits: number; name: string }>({ show: false, credits: 0, name: '' });
    const [editingCampaign, setEditingCampaign] = useState<TemplateCampaign | null>(null);
    const [editFormData, setEditFormData] = useState<Partial<TemplateCampaignUpdate>>({});

    // Form state
    const [formData, setFormData] = useState<Partial<TemplateCampaignCreate>>({
        name: '',
        template_name: '',
        target_tag_id: 0,
        interval_min_seconds: 60,
        interval_max_seconds: 120,
        start_hour: '08:00',
        end_hour: '18:00',
        weekdays: [1, 2, 3, 4, 5],
    });

    // =============================================================================
    // DATA FETCHING
    // =============================================================================

    const loadData = useCallback(async () => {
        if (!clinicId) return;
        setLoading(true);
        try {
            const [campaignsData, templatesData, tagsData] = await Promise.all([
                getTemplateCampaigns(clinicId),
                getApprovedTemplates(clinicId),
                TagsService.getTags(clinicId),
            ]);
            setCampaigns(campaignsData);
            setTemplates(templatesData);
            setTags(tagsData.filter((t: Tag) => t.category !== 'system'));

        } catch (error) {
            console.error('Erro ao carregar dados:', error);
        } finally {
            setLoading(false);
        }
    }, [clinicId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Load audience preview when tag changes
    useEffect(() => {
        const loadPreview = async () => {
            if (formData.target_tag_id && formData.target_tag_id > 0) {
                try {
                    const preview = await getAudiencePreview(clinicId, formData.target_tag_id);
                    setAudiencePreview(preview);
                } catch (error) {
                    console.error('Erro ao carregar preview:', error);
                    setAudiencePreview(null);
                }
            } else {
                setAudiencePreview(null);
            }
        };
        loadPreview();
    }, [formData.target_tag_id, clinicId]);

    // =============================================================================
    // ACTIONS
    // =============================================================================

    const handleCreate = async () => {
        if (!formData.name || !formData.template_name || !formData.target_tag_id) {
            alert('Preencha todos os campos obrigatórios');
            return;
        }

        try {
            setActionLoading(-1);
            await createTemplateCampaign(clinicId, formData as TemplateCampaignCreate);
            setShowCreateModal(false);
            setFormData({
                name: '',
                template_name: '',
                target_tag_id: 0,
                interval_min_seconds: 36,
                interval_max_seconds: 120,
                start_hour: '08:00',
                end_hour: '18:00',
                weekdays: [1, 2, 3, 4, 5],
            });
            await loadData();
        } catch (error: any) {
            alert(error.response?.data?.detail || 'Erro ao criar campanha');
        } finally {
            setActionLoading(null);
        }
    };

    const handleLaunch = async (campaignId: number) => {
        if (!window.confirm('Iniciar o disparo da campanha?')) return;
        try {
            setActionLoading(campaignId);
            await launchTemplateCampaign(clinicId, campaignId);
            await loadData();
        } catch (error: any) {
            alert(error.response?.data?.detail || 'Erro ao iniciar campanha');
        } finally {
            setActionLoading(null);
        }
    };

    const handlePause = async (campaignId: number) => {
        try {
            setActionLoading(campaignId);
            await pauseTemplateCampaign(clinicId, campaignId);
            await loadData();
        } catch (error: any) {
            alert(error.response?.data?.detail || 'Erro ao pausar campanha');
        } finally {
            setActionLoading(null);
        }
    };

    const handleResume = async (campaignId: number) => {
        try {
            setActionLoading(campaignId);
            await resumeTemplateCampaign(clinicId, campaignId);
            await loadData();
        } catch (error: any) {
            alert(error.response?.data?.detail || 'Erro ao retomar campanha');
        } finally {
            setActionLoading(null);
        }
    };

    const handleEdit = (campaign: TemplateCampaign) => {
        setEditingCampaign(campaign);
        setEditFormData({
            interval_min_seconds: campaign.interval_min_seconds,
            interval_max_seconds: campaign.interval_max_seconds,
            start_hour: campaign.start_hour,
            end_hour: campaign.end_hour,
            weekdays: campaign.weekdays,
        });
    };

    const handleSaveEdit = async () => {
        if (!editingCampaign) return;
        try {
            setActionLoading(editingCampaign.id);
            await updateTemplateCampaign(clinicId, editingCampaign.id, editFormData);
            await loadData();
            setEditingCampaign(null);
            alert('Campanha atualizada! Os agendamentos foram recalculados.');
        } catch (error: any) {
            alert(error.response?.data?.detail || 'Erro ao atualizar campanha');
        } finally {
            setActionLoading(null);
        }
    };

    const toggleEditWeekday = (day: number) => {
        setEditFormData(prev => {
            const current = prev.weekdays || [];
            if (current.includes(day)) {
                return { ...prev, weekdays: current.filter(d => d !== day) };
            }
            return { ...prev, weekdays: [...current, day].sort() };
        });
    };

    const handleDelete = async (campaignId: number) => {
        // Find the campaign to check credits
        const campaign = campaigns.find(c => c.id === campaignId);

        if (campaign && (campaign.credits_consumed || 0) > 0) {
            setDeleteBlockedModal({
                show: true,
                credits: campaign.credits_consumed,
                name: campaign.name
            });
            return;
        }

        if (!window.confirm('Tem certeza que deseja excluir esta campanha?')) return;
        try {
            setActionLoading(campaignId);
            await deleteTemplateCampaign(clinicId, campaignId);
            await loadData();
        } catch (error: any) {
            alert(error.response?.data?.detail || 'Erro ao excluir campanha');
        } finally {
            setActionLoading(null);
        }
    };

    const toggleExpand = async (campaignId: number) => {
        if (expandedCampaignId === campaignId) {
            setExpandedCampaignId(null);
            return;
        }

        setExpandedCampaignId(campaignId);

        // Fetch executions if not already loaded
        if (!executionsMap[campaignId]) {
            setLoadingExecutions(prev => ({ ...prev, [campaignId]: true }));
            try {
                const data = await getCampaignExecutions(clinicId, campaignId, 100); // Fetch first 100
                setExecutionsMap(prev => ({ ...prev, [campaignId]: data.executions }));
            } catch (error) {
                console.error("Failed to load executions", error);
            } finally {
                setLoadingExecutions(prev => ({ ...prev, [campaignId]: false }));
            }
        }
    };

    const toggleWeekday = (day: number) => {
        setFormData(prev => {
            const current = prev.weekdays || [];
            if (current.includes(day)) {
                return { ...prev, weekdays: current.filter(d => d !== day) };
            }
            return { ...prev, weekdays: [...current, day].sort() };
        });
    };

    // =============================================================================
    // RENDER
    // =============================================================================

    return (
        <div className={`min-h-screen p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {/* Header Card */}
            <div className={`relative rounded-2xl border p-6 shadow-sm mb-6 max-w-7xl mx-auto ${isDark ? 'border-gray-700 bg-gray-800' : 'border-slate-200 bg-white'
                }`}>
                <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDark ? 'bg-gray-700/50 text-gray-300' : 'bg-slate-100 text-slate-600'
                        }`}>
                        <Target className="w-7 h-7" />
                    </div>
                    <div className="flex-1">
                        <h1 className={`text-xl md:text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'
                            }`}>
                            Campanhas WhatsApp
                        </h1>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                            Dispare mensagens em massa usando templates aprovados
                        </p>
                    </div>
                    <button
                        onClick={async () => {
                            setSyncing(true);
                            try {
                                const result = await syncWhatsAppTemplates(clinicId);
                                // Reload templates after sync
                                const templatesData = await getApprovedTemplates(clinicId);
                                setTemplates(templatesData);

                                // Show warning if category changes detected
                                if (result.stats?.category_changes && result.stats.category_changes.length > 0) {
                                    let msg = '⚠️ Templates com categoria alterada pela Meta:';
                                    result.stats.category_changes.forEach((change: any) => {
                                        msg += `\n• ${change.name}: ${change.old} → ${change.new}`;
                                    });
                                    alert(msg);
                                }
                            } catch (error) {
                                console.error('Erro ao sincronizar templates:', error);
                            }
                            setSyncing(false);
                            setShowCreateModal(true);
                        }}
                        disabled={templates.length === 0 || syncing}
                        className="inline-flex items-center gap-2 rounded-xl bg-sminy-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-sminy-primary-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {syncing ? (
                            <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                Sincronizando...
                            </>
                        ) : (
                            <>
                                <Plus className="w-4 h-4" />
                                Nova Campanha
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto">

                {/* Templates warning */}
                {templates.length === 0 && !loading && (
                    <div className={`mb-6 p-4 rounded-xl border ${isDark ? 'bg-yellow-900/20 border-yellow-500/30' : 'bg-yellow-50 border-yellow-200'}`}>
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className={`font-medium ${isDark ? 'text-yellow-400' : 'text-yellow-800'}`}>
                                    Nenhum template de campanha disponível
                                </h3>
                                <p className={`text-sm mt-1 ${isDark ? 'text-yellow-300/70' : 'text-yellow-700'}`}>
                                    Para criar campanhas, você precisa de templates aprovados com use_case "campaign".
                                    Crie um template em <strong>Configurações → Biblioteca → Templates WhatsApp</strong> e aguarde a aprovação da Meta.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Campaigns List */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="w-12 h-12 border-4 border-sminy-primary/30 border-t-sminy-primary rounded-full animate-spin"></div>
                    </div>
                ) : campaigns.length === 0 ? (
                    <div className={`text-center py-20 rounded-2xl border-2 border-dashed 
                        ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-white'}`}>
                        <Target className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                        <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Nenhuma campanha criada
                        </h3>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Clique em "Nova Campanha" para começar
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {campaigns.map(campaign => (
                            <div
                                key={campaign.id}
                                className={`p-5 rounded-2xl border transition-all duration-200 shadow-xl
                         ${isDark
                                        ? 'bg-gray-800 border-gray-600'
                                        : 'bg-white border-gray-200'}`}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className={`text-lg font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                {campaign.name}
                                            </h3>
                                            <StatusBadge status={campaign.status} />
                                        </div>

                                        <div className="flex flex-wrap items-center gap-4 text-sm">
                                            <span className={`flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <MessageSquare className="w-4 h-4" />
                                                {campaign.template_name}
                                            </span>
                                            <span className={`flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <Target className="w-4 h-4" />
                                                {campaign.target_tag_name || `Tag ${campaign.target_tag_id}`}
                                            </span>
                                            <span className={`flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <Users className="w-4 h-4" />
                                                {campaign.total_contacts} contatos
                                            </span>
                                            <span className={`flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <Clock className="w-4 h-4" />
                                                {campaign.start_hour} - {campaign.end_hour}
                                            </span>
                                        </div>

                                        {/* Progress for non-draft campaigns */}
                                        {campaign.status !== 'draft' && (
                                            <div className="mt-3 max-w-md">
                                                <ProgressBar
                                                    sent={campaign.sent_count}
                                                    failed={campaign.failed_count}
                                                    total={campaign.total_contacts}
                                                />
                                                {/* Response Rate */}
                                                {campaign.sent_count > 0 && (
                                                    <div className="mt-2 flex items-center gap-3 text-xs">
                                                        <span className={`flex items-center gap-1 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                                                            💬 {campaign.response_count || 0} respostas
                                                        </span>
                                                        <span className={`font-medium ${(campaign.response_rate || 0) >= 20
                                                            ? isDark ? 'text-green-400' : 'text-green-600'
                                                            : (campaign.response_rate || 0) >= 10
                                                                ? isDark ? 'text-yellow-400' : 'text-yellow-600'
                                                                : isDark ? 'text-gray-400' : 'text-gray-500'
                                                            }`}>
                                                            {(campaign.response_rate || 0).toFixed(1)}% taxa de resposta
                                                        </span>
                                                        <span className={`flex items-center gap-1 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                                                            💰 {campaign.credits_consumed || 0} créditos
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2">
                                        {/* Expand/Collapse Button */}
                                        <button
                                            onClick={() => toggleExpand(campaign.id)}
                                            className={`p-2.5 rounded-xl transition-colors ${expandedCampaignId === campaign.id
                                                ? 'bg-sminy-primary/20 text-sminy-primary'
                                                : isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                                                }`}
                                            title={expandedCampaignId === campaign.id ? "Recolher detalhes" : "Ver detalhes de envio"}
                                        >
                                            {expandedCampaignId === campaign.id ? (
                                                <ChevronUp className="w-5 h-5" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5" />
                                            )}
                                        </button>

                                        {campaign.status === 'draft' && (
                                            <>
                                                <button
                                                    onClick={() => handleLaunch(campaign.id)}
                                                    disabled={actionLoading === campaign.id}
                                                    className="p-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white
                                   transition-colors disabled:opacity-50"
                                                    title="Iniciar Campanha"
                                                >
                                                    {actionLoading === campaign.id ? (
                                                        <RefreshCw className="w-5 h-5 animate-spin" />
                                                    ) : (
                                                        <Play className="w-5 h-5" />
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(campaign.id)}
                                                    disabled={actionLoading === campaign.id}
                                                    className={`p-2.5 rounded-xl transition-colors disabled:opacity-50
                                   ${isDark ? 'bg-gray-700 hover:bg-red-600 text-gray-300' : 'bg-gray-100 hover:bg-red-500 text-gray-600 hover:text-white'}`}
                                                    title="Excluir"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            </>
                                        )}

                                        {campaign.status === 'running' && (
                                            <button
                                                onClick={() => handlePause(campaign.id)}
                                                disabled={actionLoading === campaign.id}
                                                className="p-2.5 rounded-xl bg-yellow-500 hover:bg-yellow-600 text-white
                                 transition-colors disabled:opacity-50"
                                                title="Pausar"
                                            >
                                                {actionLoading === campaign.id ? (
                                                    <RefreshCw className="w-5 h-5 animate-spin" />
                                                ) : (
                                                    <Pause className="w-5 h-5" />
                                                )}
                                            </button>
                                        )}

                                        {campaign.status === 'paused' && (
                                            <>
                                                <button
                                                    onClick={() => handleEdit(campaign)}
                                                    className={`p-2.5 rounded-xl transition-colors ${isDark ? 'bg-gray-700 hover:bg-blue-600 text-gray-300' : 'bg-gray-100 hover:bg-blue-500 text-gray-600 hover:text-white'}`}
                                                    title="Editar Intervalos"
                                                >
                                                    <Edit className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleResume(campaign.id)}
                                                    disabled={actionLoading === campaign.id}
                                                    className="p-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white
                                 transition-colors disabled:opacity-50"
                                                    title="Retomar"
                                                >
                                                    {actionLoading === campaign.id ? (
                                                        <RefreshCw className="w-5 h-5 animate-spin" />
                                                    ) : (
                                                        <Play className="w-5 h-5" />
                                                    )}
                                                </button>
                                            </>
                                        )}

                                        {(campaign.status === 'completed' || campaign.status === 'paused' || campaign.status === 'canceled') && (
                                            <button
                                                onClick={() => handleDelete(campaign.id)}
                                                disabled={actionLoading === campaign.id}
                                                className={`p-2.5 rounded-xl transition-colors disabled:opacity-50
                                 ${isDark ? 'bg-gray-700 hover:bg-red-600 text-gray-300' : 'bg-gray-100 hover:bg-red-500 text-gray-600 hover:text-white'}`}
                                                title="Excluir"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Executions List (Expanded) */}
                                {expandedCampaignId === campaign.id && (
                                    <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                        <h4 className={`text-sm font-medium mb-3 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                            <User className="w-4 h-4" /> Detalhes de Envio
                                        </h4>

                                        {loadingExecutions[campaign.id] ? (
                                            <div className="flex justify-center p-4">
                                                <div className="w-5 h-5 border-2 border-sminy-primary/30 border-t-sminy-primary rounded-full animate-spin"></div>
                                            </div>
                                        ) : executionsMap[campaign.id]?.length > 0 ? (
                                            <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                                <div className="max-h-60 overflow-y-auto">
                                                    <table className="w-full text-sm">
                                                        <thead className={`text-xs uppercase sticky top-0 ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                                                            <tr>
                                                                <th className="px-4 py-2 text-left font-medium">Contato</th>
                                                                <th className="px-4 py-2 text-left font-medium">Status</th>
                                                                <th className="px-4 py-2 text-left font-medium">Status Entrega</th>
                                                                <th className="px-4 py-2 text-left font-medium">Agendado/Enviado</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className={`divide-y ${isDark ? 'divide-gray-700 text-gray-300' : 'divide-gray-100 text-gray-600'}`}>
                                                            {executionsMap[campaign.id].map(exec => {
                                                                // Determine delivery status hierarchy: RESPONDIDO > LIDO > ENTREGUE > FALHOU > (waiting)
                                                                let deliveryStatus = '';
                                                                let deliveryClass = '';

                                                                if (exec.responded_at) {
                                                                    deliveryStatus = 'RESPONDIDO';
                                                                    deliveryClass = isDark ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700';
                                                                } else if (exec.read_at) {
                                                                    deliveryStatus = 'LIDO';
                                                                    deliveryClass = isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700';
                                                                } else if (exec.delivered_at) {
                                                                    deliveryStatus = 'ENTREGUE';
                                                                    deliveryClass = isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700';
                                                                } else if (exec.status === 'failed') {
                                                                    deliveryStatus = 'FALHOU';
                                                                    deliveryClass = isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700';
                                                                } else if (exec.status === 'sent') {
                                                                    deliveryStatus = 'ENVIADO';
                                                                    deliveryClass = isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500';
                                                                }

                                                                return (
                                                                    <tr key={exec.id} className={isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                                                                        <td className="px-4 py-2.5">
                                                                            <div className="font-medium">{exec.contact_name || 'Sem nome'}</div>
                                                                            <div className="text-xs opacity-70 flex items-center gap-1">
                                                                                <Phone className="w-3 h-3" /> {exec.phone}
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-4 py-2.5">
                                                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium uppercase
                                                                        ${exec.status === 'sent' ? (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700') :
                                                                                    exec.status === 'failed' ? (isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700') :
                                                                                        exec.status === 'scheduled' ? (isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700') :
                                                                                            (isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600')}`}>
                                                                                {exec.status === 'sent' ? 'Enviado' :
                                                                                    exec.status === 'failed' ? 'Falha' :
                                                                                        exec.status === 'scheduled' ? 'Agendado' : 'Pendente'}
                                                                            </span>
                                                                            {exec.error_message && (
                                                                                <div className="text-[10px] text-red-500 mt-0.5 flex items-center gap-1" title={exec.error_message}>
                                                                                    <AlertTriangle className="w-3 h-3" /> Erro
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td className="px-4 py-2.5">
                                                                            {deliveryStatus ? (
                                                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium uppercase ${deliveryClass}`}>
                                                                                    {deliveryStatus === 'RESPONDIDO' && '💬 '}
                                                                                    {deliveryStatus === 'LIDO' && '👁️ '}
                                                                                    {deliveryStatus === 'ENTREGUE' && '✓✓ '}
                                                                                    {deliveryStatus === 'FALHOU' && '❌ '}
                                                                                    {deliveryStatus === 'ENVIADO' && '✓ '}
                                                                                    {deliveryStatus}
                                                                                </span>
                                                                            ) : (
                                                                                <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>-</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="px-4 py-2.5 text-xs">
                                                                            {exec.sent_at ? (
                                                                                <div title="Enviado em">
                                                                                    ✅ {new Date(exec.sent_at).toLocaleString('pt-BR')}
                                                                                </div>
                                                                            ) : exec.scheduled_at ? (
                                                                                <div title="Agendado para" className="flex items-center gap-1 text-blue-500">
                                                                                    <Clock className="w-3 h-3" /> {new Date(exec.scheduled_at).toLocaleString('pt-BR')}
                                                                                </div>
                                                                            ) : '-'}

                                                                            {exec.responded_at && (
                                                                                <div className="text-purple-500 mt-0.5 font-medium">
                                                                                    💬 Respondeu em {new Date(exec.responded_at).toLocaleString('pt-BR')}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className={`text-sm text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                Nenhum detalhe de envio encontrado.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Create Modal */}
                {showCreateModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]">
                        <div ref={createDialogRef} role="dialog" aria-modal="true" aria-label="Nova Campanha" style={{ maxHeight: 'calc(100dvh - max(1rem, env(safe-area-inset-top)) - max(1rem, env(safe-area-inset-bottom)))' }} className={`w-full max-w-[700px] flex flex-col overflow-hidden rounded-2xl shadow-2xl
                          ${isDark ? 'bg-sminy-dark-elevated' : 'bg-white'}`}>
                            {/* Header */}
                            <div className={`px-6 py-4 border-b flex items-center justify-between gap-3 shrink-0 ${isDark ? 'border-sminy-dark-border' : 'border-sminy-border'}`}>
                                <h2 className={`text-xl font-semibold ${isDark ? 'text-sminy-dark-text-primary' : 'text-sminy-text-primary'}`}>
                                    Nova Campanha
                                </h2>
                                <button onClick={() => setShowCreateModal(false)} disabled={actionLoading === -1} aria-label="Fechar" className={`min-w-11 min-h-11 flex items-center justify-center rounded-xl focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50 ${isDark ? 'hover:bg-sminy-dark-surface-subtle text-sminy-dark-text-primary' : 'hover:bg-sminy-blue-soft text-sminy-text-primary'}`}>
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Form */}
                            <div className="grid grid-cols-1 gap-6 p-6 overflow-y-auto min-h-0">
                                {/* Left Column: Basics & Template */}
                                <div className="space-y-5">
                                    {/* Name */}
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>
                                            Nome da Campanha *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name || ''}
                                            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                            className={`w-full min-h-11 px-4 py-2.5 rounded-xl border transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary
                                 ${isDark
                                                    ? 'bg-sminy-dark-surface border-sminy-dark-border text-sminy-dark-text-primary placeholder-gray-400'
                                                    : 'bg-white border-sminy-border text-sminy-text-primary placeholder-gray-500'}`}
                                            placeholder="Ex: Promoção Janeiro 2026"
                                        />
                                    </div>

                                    {/* Template */}
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>
                                            Template *
                                        </label>
                                        <select
                                            value={formData.template_name || ''}
                                            onChange={e => {
                                                setFormData(prev => ({ ...prev, template_name: e.target.value }));
                                            }}
                                            className={`w-full min-h-11 px-4 py-2.5 rounded-xl border transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary
                                 ${isDark
                                                    ? 'bg-sminy-dark-surface border-sminy-dark-border text-sminy-dark-text-primary'
                                                    : 'bg-white border-sminy-border text-sminy-text-primary'}`}
                                        >
                                            <option value="">Selecione um template</option>
                                            {templates.map(t => (
                                                <option
                                                    key={t.id}
                                                    value={t.name}
                                                >
                                                    {t.name} · {t.category}
                                                </option>
                                            ))}
                                        </select>

                                        {/* Template Preview */}
                                        {formData.template_name && (() => {
                                            const selectedTemplate = templates.find(t => t.name === formData.template_name);
                                            return (
                                                <div className="mt-3 space-y-2">
                                                    {/* Body Preview */}
                                                    {selectedTemplate?.body_text && (
                                                        <div className={`p-4 rounded-xl border ${isDark ? 'bg-sminy-dark-surface-subtle border-sminy-dark-border' : 'bg-sminy-blue-soft border-sminy-border'}`}>
                                                            <div className="flex items-start gap-2">
                                                                <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0 text-sminy-primary" />
                                                                <div>
                                                                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>
                                                                        Preview da mensagem:
                                                                    </p>
                                                                    <p className={`text-sm ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'} line-clamp-[10]`}>
                                                                        {selectedTemplate.body_text}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* Right Column: Audience & Settings */}
                                <div className="space-y-5">
                                    {/* Tag */}
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>
                                            Tag de Público *
                                        </label>
                                        <select
                                            value={formData.target_tag_id || ''}
                                            onChange={e => setFormData(prev => ({ ...prev, target_tag_id: parseInt(e.target.value) || 0 }))}
                                            className={`w-full min-h-11 px-4 py-2.5 rounded-xl border transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary
                                 ${isDark
                                                    ? 'bg-sminy-dark-surface border-sminy-dark-border text-sminy-dark-text-primary'
                                                    : 'bg-white border-sminy-border text-sminy-text-primary'}`}
                                        >
                                            <option value="">Selecione uma tag</option>
                                            {tags.map(t => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                        </select>

                                        {/* Audience Preview */}
                                        {audiencePreview && (
                                            <div className={`mt-2 p-4 rounded-xl ${isDark ? 'bg-sminy-dark-surface-subtle' : 'bg-sminy-blue-soft'}`}>
                                                <div className="flex items-center gap-2">
                                                    <Users className="w-4 h-4 text-sminy-primary" />
                                                    <span className={`text-sm font-medium ${isDark ? 'text-sminy-dark-text-primary' : 'text-sminy-text-primary'}`}>
                                                        {audiencePreview.contact_count} contatos receberão a mensagem
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        <p className={`mt-3 p-4 rounded-xl border text-xs ${isDark ? 'bg-sminy-dark-surface-subtle border-sminy-dark-border text-sminy-dark-text-secondary' : 'bg-sminy-blue-soft border-sminy-border text-sminy-text-secondary'}`}>
                                            As tarifas do WhatsApp Oficial são cobradas diretamente pela Meta.
                                        </p>
                                    </div>

                                    {/* Schedule & Interval */}
                                    <div className={`p-4 rounded-xl border space-y-4 ${isDark ? 'border-sminy-dark-border bg-sminy-dark-surface-subtle' : 'border-sminy-border bg-sminy-blue-soft'}`}>
                                        {/* Time Window */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>
                                                    Horário Início
                                                </label>
                                                <input
                                                    type="time"
                                                    value={formData.start_hour || '08:00'}
                                                    onChange={e => setFormData(prev => ({ ...prev, start_hour: e.target.value }))}
                                                    className={`w-full min-h-11 px-3 py-2 rounded-xl border text-sm transition-colors
                                       ${isDark
                                                            ? 'bg-sminy-dark-surface border-sminy-dark-border text-sminy-dark-text-primary'
                                                            : 'bg-white border-sminy-border text-sminy-text-primary'}`}
                                                />
                                            </div>
                                            <div>
                                                <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>
                                                    Horário Fim
                                                </label>
                                                <input
                                                    type="time"
                                                    value={formData.end_hour || '18:00'}
                                                    onChange={e => setFormData(prev => ({ ...prev, end_hour: e.target.value }))}
                                                    className={`w-full min-h-11 px-3 py-2 rounded-xl border text-sm transition-colors
                                       ${isDark
                                                            ? 'bg-sminy-dark-surface border-sminy-dark-border text-sminy-dark-text-primary'
                                                            : 'bg-white border-sminy-border text-sminy-text-primary'}`}
                                                />
                                            </div>
                                        </div>

                                        {/* Weekdays */}
                                        <div>
                                            <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>
                                                Dias da Semana
                                            </label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {WEEKDAYS.map(day => (
                                                    <button
                                                        key={day.value}
                                                        type="button"
                                                        onClick={() => toggleWeekday(day.value)}
                                                        className={`w-9 h-11 rounded-xl focus-visible:ring-2 focus-visible:ring-sminy-primary font-medium text-xs transition-all
                                         ${formData.weekdays?.includes(day.value)
                                                                ? 'bg-sminy-blue-light text-sminy-primary'
                                                                : isDark
                                                                    ? 'bg-sminy-dark-surface border border-sminy-dark-border text-sminy-dark-text-secondary hover:bg-sminy-dark-surface-subtle'
                                                                    : 'bg-white border border-sminy-border text-sminy-text-secondary hover:bg-sminy-blue-soft'}`}
                                                        title={day.label}
                                                    >
                                                        {day.short}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Interval */}
                                        <div className="pt-2 border-t border-dashed border-gray-200 dark:border-gray-700">
                                            <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>
                                                Intervalo (minutos)
                                            </label>
                                            <div className="grid grid-cols-2 gap-3 mb-2">
                                                <div>
                                                    <span className="text-[10px] text-gray-500 mb-0.5 block">Mínimo</span>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        max={10}
                                                        value={Math.round((formData.interval_min_seconds || 60) / 60)}
                                                        onChange={e => {
                                                            const minMinutes = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                                                            const minSeconds = minMinutes * 60;
                                                            const currentMaxSeconds = formData.interval_max_seconds || 120;
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                interval_min_seconds: minSeconds,
                                                                interval_max_seconds: Math.max(currentMaxSeconds, minSeconds)
                                                            }));
                                                        }}
                                                        className={`w-full min-h-11 px-2 py-1.5 rounded-xl border text-center text-sm ${isDark ? 'bg-sminy-dark-surface border-sminy-dark-border text-sminy-dark-text-primary' : 'bg-white border-sminy-border'}`}
                                                    />
                                                </div>
                                                <div>
                                                    <span className="text-[10px] text-gray-500 mb-0.5 block">Máximo</span>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        max={30}
                                                        value={Math.round((formData.interval_max_seconds || 120) / 60)}
                                                        onChange={e => {
                                                            const maxMinutes = Math.max(1, Math.min(30, parseInt(e.target.value) || 2));
                                                            const maxSeconds = maxMinutes * 60;
                                                            const currentMinSeconds = formData.interval_min_seconds || 60;
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                interval_max_seconds: maxSeconds,
                                                                interval_min_seconds: Math.min(currentMinSeconds, maxSeconds)
                                                            }));
                                                        }}
                                                        className={`w-full min-h-11 px-2 py-1.5 rounded-xl border text-center text-sm ${isDark ? 'bg-sminy-dark-surface border-sminy-dark-border text-sminy-dark-text-primary' : 'bg-white border-sminy-border'}`}
                                                    />
                                                </div>
                                            </div>

                                            {/* Rate Info Compact */}
                                            {(() => {
                                                const minMinutes = (formData.interval_min_seconds || 60) / 60;
                                                const maxMinutes = (formData.interval_max_seconds || 120) / 60;
                                                const avgMinutes = (minMinutes + maxMinutes) / 2;
                                                const msgsPerHourAvg = Math.round(60 / avgMinutes);

                                                return (
                                                    <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                                        <AlertCircle className="w-3 h-3 text-sminy-primary" />
                                                        <span>
                                                            Est. <strong>{msgsPerHourAvg} msgs/h</strong> (limite 120/h)
                                                        </span>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className={`px-6 py-4 border-t flex justify-end gap-3 shrink-0 ${isDark ? 'border-sminy-dark-border' : 'border-sminy-border'}`}>
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    disabled={actionLoading === -1}
                                    className={`min-h-11 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50
                                       ${isDark
                                            ? 'bg-sminy-dark-surface border-sminy-dark-border text-sminy-dark-text-primary hover:bg-sminy-dark-surface-subtle'
                                            : 'border-sminy-border text-sminy-text-primary hover:bg-sminy-blue-soft'}`}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCreate}
                                    disabled={
                                        actionLoading === -1 ||
                                        !formData.name ||
                                        !formData.template_name ||
                                        !formData.target_tag_id
                                    }
                                    className="min-h-11 flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-sminy-primary hover:bg-sminy-primary-hover active:bg-sminy-primary-pressed focus-visible:ring-2 focus-visible:ring-sminy-primary
                                       text-white rounded-xl font-medium transition-colors
                                       text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {actionLoading === -1 ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : null}
                                    Criar Campanha
                                </button>
                            </div>
                        </div>
                    </div>
                )
                }

                {/* Edit Campaign Modal */}
                {editingCampaign && (
                    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className={`rounded-2xl shadow-xl w-full max-w-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                <div className="flex items-center justify-between">
                                    <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        Editar Campanha
                                    </h2>
                                    <button
                                        onClick={() => setEditingCampaign(null)}
                                        className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                                    >
                                        ✕
                                    </button>
                                </div>
                                <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {editingCampaign.name}
                                </p>
                            </div>

                            <div className="px-6 py-5 space-y-5">
                                {/* Interval - Same as create modal */}
                                <div className="pt-2 border-t border-dashed border-gray-200 dark:border-gray-700">
                                    <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Intervalo (minutos)
                                    </label>
                                    <div className="grid grid-cols-2 gap-3 mb-2">
                                        <div>
                                            <span className="text-[10px] text-gray-500 mb-0.5 block">Mínimo</span>
                                            <input
                                                type="number"
                                                min={1}
                                                max={10}
                                                value={Math.round((editFormData.interval_min_seconds || 60) / 60)}
                                                onChange={e => {
                                                    const minMinutes = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                                                    const minSeconds = minMinutes * 60;
                                                    const currentMaxSeconds = editFormData.interval_max_seconds || 120;
                                                    setEditFormData(prev => ({
                                                        ...prev,
                                                        interval_min_seconds: minSeconds,
                                                        interval_max_seconds: Math.max(currentMaxSeconds, minSeconds)
                                                    }));
                                                }}
                                                className={`w-full px-2 py-1.5 rounded-lg border text-center text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                                            />
                                        </div>
                                        <div>
                                            <span className="text-[10px] text-gray-500 mb-0.5 block">Máximo</span>
                                            <input
                                                type="number"
                                                min={1}
                                                max={30}
                                                value={Math.round((editFormData.interval_max_seconds || 120) / 60)}
                                                onChange={e => {
                                                    const maxMinutes = Math.max(1, Math.min(30, parseInt(e.target.value) || 2));
                                                    const maxSeconds = maxMinutes * 60;
                                                    const currentMinSeconds = editFormData.interval_min_seconds || 60;
                                                    setEditFormData(prev => ({
                                                        ...prev,
                                                        interval_max_seconds: maxSeconds,
                                                        interval_min_seconds: Math.min(currentMinSeconds, maxSeconds)
                                                    }));
                                                }}
                                                className={`w-full px-2 py-1.5 rounded-lg border text-center text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                                            />
                                        </div>
                                    </div>
                                    <p className={`text-xs ${isDark ? 'text-yellow-400/70' : 'text-yellow-600'}`}>
                                        💡 Aumente para 2-3 minutos para reduzir bloqueios da Meta
                                    </p>
                                </div>

                                {/* Time Window */}
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Janela de envio
                                    </label>
                                    <div className="flex gap-3 items-center">
                                        <input
                                            type="time"
                                            value={editFormData.start_hour || '08:00'}
                                            onChange={(e) => setEditFormData(prev => ({ ...prev, start_hour: e.target.value }))}
                                            className={`px-3 py-2 rounded-lg border ${isDark
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                                }`}
                                        />
                                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>até</span>
                                        <input
                                            type="time"
                                            value={editFormData.end_hour || '18:00'}
                                            onChange={(e) => setEditFormData(prev => ({ ...prev, end_hour: e.target.value }))}
                                            className={`px-3 py-2 rounded-lg border ${isDark
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                                }`}
                                        />
                                    </div>
                                </div>

                                {/* Weekdays */}
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Dias de envio
                                    </label>
                                    <div className="flex gap-2">
                                        {WEEKDAYS.map(day => (
                                            <button
                                                key={day.value}
                                                type="button"
                                                onClick={() => toggleEditWeekday(day.value)}
                                                className={`w-10 h-10 rounded-lg font-medium text-sm transition-colors ${(editFormData.weekdays || []).includes(day.value)
                                                    ? 'bg-sminy-primary text-white'
                                                    : isDark ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {day.short}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Info */}
                                <div className={`p-3 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                                    <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                                        💡 Ao salvar, os agendamentos pendentes serão recalculados com os novos intervalos.
                                    </p>
                                </div>
                            </div>

                            <div className={`px-6 py-4 border-t flex justify-end gap-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                <button
                                    onClick={() => setEditingCampaign(null)}
                                    className={`px-4 py-2.5 rounded-xl font-medium transition-colors ${isDark
                                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSaveEdit}
                                    disabled={actionLoading === editingCampaign.id}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-sminy-primary hover:bg-sminy-primary-hover text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                                >
                                    {actionLoading === editingCampaign.id ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <CheckCircle className="w-4 h-4" />
                                    )}
                                    Salvar e Recalcular
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Blocked Modal */}
                {deleteBlockedModal.show && (
                    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className={`rounded-2xl shadow-xl w-full max-w-md ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                        <AlertTriangle className="w-5 h-5 text-orange-600" />
                                    </div>
                                    <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        Exclusão Não Permitida
                                    </h2>
                                </div>
                            </div>

                            <div className="px-6 py-5">
                                <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                    A campanha <strong className={isDark ? 'text-white' : 'text-gray-900'}>"{deleteBlockedModal.name}"</strong> não pode ser excluída.
                                </p>

                                <div className={`p-4 rounded-xl ${isDark ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="text-2xl">💰</span>
                                        <span className={`text-lg font-bold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                                            {deleteBlockedModal.credits} créditos consumidos
                                        </span>
                                    </div>
                                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Os dados de consumo são mantidos para histórico e auditoria financeira.
                                    </p>
                                </div>
                            </div>

                            <div className={`px-6 py-4 border-t flex justify-end ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                <button
                                    onClick={() => setDeleteBlockedModal({ show: false, credits: 0, name: '' })}
                                    className="rounded-xl bg-sminy-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-sminy-primary-hover transition-colors"
                                >
                                    Entendi
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div >
    );
};

export default CampanhasPage;
